import { useState } from "react";
import { useSelector } from "react-redux";

import {
  useGetBankByIbanMutation,
  useCreateBankMutation,
  useCreateBankDraftMutation,
  useUpdateBankMutation,
  useUpdateBankDraftMutation,
} from "../../../../store/Company/companyApiSlice";
import { isVerified } from "../../../../utils/Helper";
import { uploadFilePath } from "../../../../constants/s3UploadPath";
import { uploadFileToS3 } from "../../../../utils/uploadFileToS3";
import { CommonToast } from "../../../common/Toast";
import useCompanyFollowUpdate from "../../Dashboard/mycompanies/CompleteCompanyProfile/useCompanyFollowUpdate";
import { BANK_SPEC, bankFromLookup, lookupMatched, validateIban } from "./bankingFields";

/* Add / edit a bank account. */
export const useBankForm = ({ editing, onSaved }) => {
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);
  const companyID = currentEditingCompany?.companyID;

  const [getBankByIban, { isLoading: isLooking }] = useGetBankByIbanMutation();
  const [createBank] = useCreateBankMutation();
  const [createBankDraft] = useCreateBankDraftMutation();
  const [updateBank] = useUpdateBankMutation();
  const [updateBankDraft] = useUpdateBankDraftMutation();
  const followUpdate = useCompanyFollowUpdate();

  const [iban, setIban] = useState("");
  /* The matched bank: null until the lookup succeeds. An existing row is
     already matched, so editing starts on step two. */
  const [matched, setMatched] = useState(
    editing
      ? {
          IBAN: String(editing?.IBAN || "").toUpperCase(),
          bank_name: editing?.bank_name || "",
          country: editing?.country || "",
        }
      : null
  );
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const search = async () => {
    const problem = validateIban(iban);
    if (problem) {
      setErrors({ iban: problem });
      return;
    }
    setErrors({});

    try {
      const res = await getBankByIban({ iban: iban.trim(), token });
      /* Transcribed: only a response carrying `data.data` counts as a match.
         The live code left every other outcome silent, so an unrecognised
         IBAN looked like a dead button. */
      if (lookupMatched(res)) {
        setMatched(bankFromLookup(res.data));
        return;
      }
      setErrors({
        iban:
          res?.error?.data?.detail ||
          "We couldn't match that IBAN to a bank. Check it and try again, or contact support.",
      });
    } catch {
      setErrors({ iban: "Couldn't reach the bank lookup. Please try again." });
    }
  };

  /* Back to step one to try a different IBAN. Not offered while editing — the
     live edit path has no lookup step. */
  const reset = () => {
    setMatched(null);
    setErrors({});
  };

  const submit = async () => {
    if (!matched) return;
    setBusy(true);

    try {
      /* Transcribed field for field, including the uppercase IBAN. FormData
         and not JSON because the bank confirmation letter rides along. */
      const body = new FormData();
      body.append("bank_name", matched.bank_name);
      body.append("IBAN", String(matched.IBAN || "").toUpperCase());
      body.append("country", matched.country);

      if (file) {
        const uploaded = await uploadFileToS3(
          uploadFilePath?.bank,
          file,
          token,
          companyID,
          "bank_letter"
        );
        if (uploaded?.success === false) {
          /* uploadFileToS3 reports its own reason via a toast and resolves
             with success:false rather than throwing. */
          setBusy(false);
          return;
        }
        body.append("bank_conf_letter", uploaded?.url);
      }

      if (isVerified(currentEditingCompany?.verifiedCompany)) {
        /* Transcribed: the draft path always carries current_companyID and a
           request_type, and links back to the live row when there is one. */
        body.append("current_companyID", companyID);

        if (editing?.[BANK_SPEC.draftIdKey]) {
          body.append("request_type", BANK_SPEC.updateRequestType);
          if (editing?.[BANK_SPEC.draftLinkKey] != null) {
            body.append("bankID", editing[BANK_SPEC.draftLinkKey]);
          }
          await updateBankDraft({
            data: body,
            bankId: editing[BANK_SPEC.draftIdKey],
            token,
          }).unwrap();
        } else {
          body.append("request_type", BANK_SPEC.createRequestType);
          if (editing?.[BANK_SPEC.idKey] != null) {
            body.append("bankID", editing[BANK_SPEC.idKey]);
          }
          await createBankDraft({ data: body, token }).unwrap();
        }

        CommonToast.success("Sent for review. The team confirms the change shortly.");
        onSaved?.();
        return;
      }

      if (editing) {
        await updateBank({
          data: body,
          bankId: editing[BANK_SPEC.idKey],
          token,
        }).unwrap();
        await followUpdate(companyID, BANK_SPEC.followUpdated, token);
        CommonToast.success("Banking information updated.");
      } else {
        /* Transcribed: only the direct CREATE carries current_companyID —
           BankAPIView.create pops it off the payload and uses it to build the
           CompanyBank link. An update has nothing to link. */
        body.append("current_companyID", companyID);
        await createBank({ data: body, token }).unwrap();
        await followUpdate(companyID, BANK_SPEC.followCreated, token);
        CommonToast.success("Banking information added.");
      }

      onSaved?.();
    } catch (error) {
      /* The live handlers swallow every failure in an empty catch and then
         close the dialog regardless, so a rejected save looked like a saved
         one. */
      CommonToast.error(
        error?.data?.detail ||
          error?.data?.message ||
          error?.message ||
          "Couldn't save this bank account. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return {
    iban,
    setIban,
    matched,
    file,
    setFile,
    errors,
    setErrors,
    isLooking,
    busy,
    search,
    reset,
    submit,
  };
};

export default useBankForm;
